import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

const addPhoneNumberSenderFormSchema = z.object({
    phoneNumber: z
        .string()
        .min(1, "Phone number is required")
        .regex(/^\+?[1-9]\d{1,14}$/, "Please provide a valid phone number"),
});

type AddPhoneNumberSenderFormValues = z.infer<
    typeof addPhoneNumberSenderFormSchema
>;

const fields = createFormikFields<AddPhoneNumberSenderFormValues>();

interface AddPhoneNumberSenderFormProps {
    onSubmit: (phoneNumber: string) => void;
    onCancel: () => void;
}

export const AddPhoneNumberSenderForm: React.FC<
    AddPhoneNumberSenderFormProps
> = ({ onSubmit, onCancel }) => {
    const formik = useFormik<AddPhoneNumberSenderFormValues>({
        initialValues: {
            phoneNumber: "",
        },
        validate: validateFormikWithZodSchema(addPhoneNumberSenderFormSchema),
        onSubmit: submitHelper({
            handler: async ({ phoneNumber }) => {
                onSubmit(phoneNumber.trim());
            },
        }),
    });

    return (
        <FormikProvider value={formik}>
            <Form>
                <VStack gap={16}>
                    <fields.Text
                        name="phoneNumber"
                        type="tel"
                        label="Phone Number"
                        placeholder="+1234567890"
                        size="xl"
                    />
                    <div>
                        <p className="text-muted text-small">
                            We'll send a verification code to this phone number
                            to confirm ownership.
                        </p>

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onCancel}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                disabled={!formik.isValid}
                                loading={formik.isSubmitting}
                                type="submit"
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Add phone number",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                    scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                }}
                            >
                                Next
                            </ButtonWithLoader>
                        </HStack>
                    </div>
                </VStack>
            </Form>
        </FormikProvider>
    );
};
